import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { supabase } from "../supabaseClient";

const ACCEPTED_EXT = [".glb"];
const MAX_SIZE_MB = 25;

/**
 * Komponen upload model 3D produk (.glb) ke Supabase Storage.
 * Props:
 * - value: string (model_url saat ini, bisa null/empty)
 * - onChange: (newUrl: string|null) => void
 * - productId: string
 */
export default function ModelUploadField({ value, onChange, productId, rotationY = 0 }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [showManualUrl, setShowManualUrl] = useState(false);

  const validateFile = (file) => {
    const lowerName = file.name.toLowerCase();
    const hasValidExt = ACCEPTED_EXT.some((ext) => lowerName.endsWith(ext));
    if (!hasValidExt) {
      return "Format file harus .glb (GLTF Binary).";
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `Ukuran file maksimal ${MAX_SIZE_MB}MB.`;
    }
    return null;
  };

  const uploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setUploading(true);

    try {
      const safeProductId = (productId || "model").trim() || "model";
      const fileName = `${safeProductId}-${Date.now()}.glb`;

      const { error: uploadErr } = await supabase.storage
        .from("product-assets")
        .upload(fileName, file, { upsert: true, contentType: "model/gltf-binary" });

      if (uploadErr) {
        setError(`Gagal upload: ${uploadErr.message}`);
        setUploading(false);
        return;
      }

      const { data: publicUrlData } = supabase.storage
        .from("product-assets")
        .getPublicUrl(fileName);

      onChange(publicUrlData.publicUrl);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleRemove = () => {
    onChange(null);
    setError("");
  };

  return (
    <div>
      {value ? (
        <div>
          <ModelPreview3D url={value} rotationY={rotationY} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={smallBtnStyle("#3b82f6", "#eff6ff")}
            >
              {uploading ? "Mengupload..." : "Ganti Model"}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              style={smallBtnStyle("#ef4444", "#fef2f2")}
            >
              Hapus (pakai geometri bawaan)
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? "#3b82f6" : "#cbd5e1"}`,
            borderRadius: 10, padding: "24px 16px", textAlign: "center",
            cursor: "pointer", background: dragOver ? "#eff6ff" : "#f8fafc",
            transition: "all 0.15s",
          }}
        >
          {uploading ? (
            <div style={{ fontSize: 13, color: "#3b82f6", fontWeight: 600 }}>Mengupload & memvalidasi...</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "#475569", fontWeight: 600, marginBottom: 4 }}>
                Klik atau tarik file .glb ke sini
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                Format GLTF Binary (.glb) — maksimal {MAX_SIZE_MB}MB
              </div>
              <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 4 }}>
                Kosongkan untuk memakai geometri 3D bawaan sistem
              </div>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".glb"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {error && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{error}</div>}

      <button
        type="button"
        onClick={() => setShowManualUrl((v) => !v)}
        style={{
          background: "none", border: "none", color: "#94a3b8", fontSize: 11,
          textDecoration: "underline", cursor: "pointer", padding: 0, marginTop: 8,
        }}
      >
        {showManualUrl ? "Sembunyikan input URL manual" : "atau isi URL model secara manual"}
      </button>

      {showManualUrl && (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="https://... URL file .glb"
          style={{
            width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1",
            fontSize: 12, marginTop: 6, outline: "none", boxSizing: "border-box",
          }}
        />
      )}
    </div>
  );
}

/**
 * Preview 3D mini menggunakan Three.js, dirender di canvas kecil di
 * dalam form admin — supaya admin bisa langsung cek model sebelum
 * disimpan, tanpa perlu buka RoomDesigner.
 */
function ModelPreview3D({ url, rotationY = 0 }) {
  const canvasRef = useRef(null);
  const modelRef = useRef(null);
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "error"

  useEffect(() => {
    if (!canvasRef.current || !url) return;
    setStatus("loading");
    modelRef.current = null;

    const canvas = canvasRef.current;
    const w = canvas.clientWidth || 280;
    const h = canvas.clientHeight || 200;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.01, 50);
    camera.position.set(0.6, 0.6, 0.6);

    scene.add(new THREE.AmbientLight("#ffffff", 1.0));
    const dir = new THREE.DirectionalLight("#ffffff", 1.2);
    dir.position.set(2, 3, 2);
    scene.add(dir);

    const grid = new THREE.GridHelper(2, 10, "#cbd5e1", "#e2e8f0");
    scene.add(grid);

    // Indikator arah "depan" (+Z) — panah kecil biru, membantu admin
    // memahami arah mana yang dianggap sistem sebagai sisi depan device.
    const arrowDir = new THREE.Vector3(0, 0, 1);
    const arrow = new THREE.ArrowHelper(arrowDir, new THREE.Vector3(0, 0.02, 0), 0.5, 0x3b82f6, 0.12, 0.08);
    scene.add(arrow);

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    let animId;
    let disposed = false;
    const loader = new GLTFLoader();

    loader.load(
      url,
      (gltf) => {
        if (disposed) return;
        const model = gltf.scene;

        // Auto-fit kamera berdasar bounding box model
        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);

        model.position.sub(center);
        model.rotation.y = (rotationY * Math.PI) / 180;
        scene.add(model);
        modelRef.current = model;

        const maxDim = Math.max(size.x, size.y, size.z, 0.01);
        const dist = maxDim * 2.2;
        camera.position.set(dist, dist * 0.8, dist);
        camera.lookAt(0, 0, 0);
        controls.target.set(0, 0, 0);
        controls.update();

        arrow.setLength(maxDim * 0.8, maxDim * 0.18, maxDim * 0.12);

        setStatus("ok");
      },
      undefined,
      () => { if (!disposed) setStatus("error"); }
    );

    const tick = () => {
      animId = requestAnimationFrame(tick);
      controls.update();
      renderer.render(scene, camera);
    };
    tick();

    return () => {
      disposed = true;
      cancelAnimationFrame(animId);
      controls.dispose();
      renderer.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Effect terpisah: update rotasi model secara real-time saat slider
  // digeser, TANPA reload ulang scene/model dari server — supaya preview
  // terasa instan & responsif.
  useEffect(() => {
    if (modelRef.current) {
      modelRef.current.rotation.y = (rotationY * Math.PI) / 180;
    }
  }, [rotationY]);

  return (
    <div style={{ position: "relative", width: "100%", height: 200, borderRadius: 10, overflow: "hidden", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
      {status === "loading" && (
        <div style={overlayStyle}>Memuat preview model...</div>
      )}
      {status === "error" && (
        <div style={{ ...overlayStyle, color: "#dc2626" }}>
          Gagal memuat model. Pastikan file .glb valid.
        </div>
      )}
    </div>
  );
}

const overlayStyle = {
  position: "absolute", inset: 0, display: "flex", alignItems: "center",
  justifyContent: "center", fontSize: 12, color: "#94a3b8", background: "rgba(248,250,252,0.85)",
};

function smallBtnStyle(color, bg) {
  return {
    padding: "6px 12px", borderRadius: 6, border: `1px solid ${color}33`,
    background: bg, color, fontSize: 12, fontWeight: 600, cursor: "pointer",
  };
}